import React, { useState } from 'react';
import classNames from 'classnames/bind';
import { useInView } from 'react-intersection-observer';
import {
  IconChromeFill,
  IconEdgeFill,
  IconFirefoxFill,
  IconIexplorerFill,
  IconSafariFill,
  IconManualFill,
  IconEyeFill,
  IconAndroidFill,
  IconIosFill,
  IconLinuxFill,
  IconMacosFill,
  IconPinFill,
  IconWindows8Fill,
  IconPrivateDeviceFill,
  IconUnpinFill,
} from '@saucelabs/ds-icons';
import { Tooltip } from '@saucelabs/ds-tooltip';
import Placeholder from '@saucelabs/sl-images/lib/placeholder.png';

import { ValueOf } from '../../../globalTypes';
import constants from '../constants';
import { useStartTestButtonState } from '../hooks/useStartTestButtonState';
import TrialDiagonal from './TrialDiagonal';
import Styles from './DeviceListItem.styles';

const context = classNames.bind(Styles);

const OsIcon = {
  [constants.OS_TYPES.LINUX]: <IconLinuxFill className="h-2 w-2" />,
  [constants.OS_TYPES.MAC_OS]: <IconMacosFill className="fill-current h-2 text-gray-600 w-2" />,
  [constants.OS_TYPES.OSX]: <IconMacosFill className="fill-current h-2 text-gray-600 w-2" />,
  [constants.OS_TYPES.WINDOWS]: <IconWindows8Fill className="h-2 w-2" />,
};

const BrowserIcon = {
  [constants.BROWSER_TYPES.CHROME]: <IconChromeFill className="h-2 w-2" />,
  [constants.BROWSER_TYPES.EDGE]: <IconEdgeFill className="h-2 w-2" />,
  [constants.BROWSER_TYPES.FIREFOX]: <IconFirefoxFill className="h-2 w-2" />,
  [constants.BROWSER_TYPES.IE]: <IconIexplorerFill className="h-2 w-2" />,
  [constants.BROWSER_TYPES.SAFARI]: <IconSafariFill className="h-2 w-2" />,
};

// TODO: check if it's still be a case that it can be
// something different that iOS or Android
const renderSystemIcons = (
  deviceOs: ValueOf<typeof constants.OS_TYPES>,
  browser: ValueOf<typeof constants.BROWSER_TYPES>,
) => {
  if (deviceOs === constants.OS_TYPES.IOS) {
    return <IconIosFill className="fill-current h-2 mr-1.5 text-gray-600 w-2" />;
  }
  if (deviceOs === constants.OS_TYPES.ANDROID) {
    return <IconAndroidFill className="fill-current h-2 mr-1.5 text-gray-600 w-2" />;
  }

  return (
    <div
      className={context('system-icon', 'double', 'fill-current', 'text-gray-600')}
      data-qa="device-list-item-system-icon"
    >
      {BrowserIcon[browser]}
      {OsIcon[deviceOs]}
    </div>
  );
};

export type DeviceListItemProps = {
  readonly browserType?: ValueOf<typeof constants.BROWSER_TYPES>;
  readonly email?: string;
  readonly hasSuitableAppConfigurationSelected?: boolean;
  readonly image?: string;
  readonly isCreditsExpired?: boolean;
  readonly isFreemium?: boolean;
  readonly isFreeTrial?: boolean;
  readonly isInUse?: boolean;
  readonly isLocked?: boolean;
  readonly isManualMinutesExpired?: boolean;
  readonly name: string;
  readonly onDetailsClick: () => void;
  readonly onLaunchClick: () => void;
  readonly onPinnedClick: () => void;
  readonly onSelectClick: () => void;
  readonly os: string;
  readonly osType: ValueOf<typeof constants.OS_TYPES>;
  readonly pinned?: boolean;
  readonly privateDevice?: boolean;
  readonly screenSize: string;
  readonly selected?: boolean;
  readonly unverified?: boolean;
};

const DeviceListItem: React.FC<DeviceListItemProps> = ({
  browserType = constants.BROWSER_TYPES.NONE,
  email,
  hasSuitableAppConfigurationSelected = true,
  image,
  isCreditsExpired = false,
  isFreemium = false,
  isFreeTrial = false,
  isInUse = false,
  isLocked = false,
  isManualMinutesExpired = false,
  name,
  onDetailsClick,
  onLaunchClick,
  onPinnedClick,
  onSelectClick,
  os,
  osType,
  pinned = false,
  privateDevice = false,
  screenSize,
  selected = false,
  unverified = false,
}) => {
  const [imageUrlBroken, setImageUrlBroken] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true });
  const { enableAdvise, isButtonDisabled, isDevicePermitted } = useStartTestButtonState({
    email,
    hasSuitableAppConfigurationSelected,
    isCreditsExpired,
    isInUse,
    isLocked,
    isManualMinutesExpired,
    unverified,
  });

  const renderDeviceTypeIndicator = () => (
    <div>
      {isInUse && (
        <div
          className={Styles['in-use']}
          data-qa="device-in-use"
        >
          in use
        </div>
      )}
      <TrialDiagonal
        isFreemium={isFreemium}
        isFreeTrial={isFreeTrial}
      />
    </div>
  );

  return (
    <div
      className={context('container', 'h-9 bg-white overflow-hidden', { selected })}
      onClick={(event) => {
        event.stopPropagation();
        onSelectClick();
      }}
      role="button"
      tabIndex={0}
    >
      <div
        ref={ref}
        className={context('column-image', 'relative')}
      >
        {(!image || !inView) && (
          <img
            alt={name}
            className="max-h-7 max-w-7"
            src={Placeholder}
          />
        )}
        {image && inView && (
          <img
            alt={name}
            className={context('image-iphone max-h-7 max-w-7', { 'filter grayscale opacity-50': isInUse })}
            onError={() => setImageUrlBroken(true)}
            src={imageUrlBroken ? Placeholder : image}
          />
        )}
      </div>

      <div className={Styles['column-device']}>
        {renderDeviceTypeIndicator()}
        <div className={Styles['device-name']}>
          <Tooltip
            delay={300}
            placement="top"
            title={name}
          >
            <span>{name}</span>
          </Tooltip>
        </div>
      </div>

      <div className={Styles['column-system']}>
        <div className={Styles['os-and-version']}>
          {renderSystemIcons(osType, browserType)}
          <Tooltip
            delay={300}
            placement="top"
            title={os}
          >
            <span>{os}</span>
          </Tooltip>
        </div>
      </div>

      <div className={Styles['column-screen-size']}>
        <div className={Styles['screen-size']}>
          <Tooltip
            delay={300}
            placement="top"
            title={screenSize}
          >
            <span>{screenSize}</span>
          </Tooltip>
        </div>
      </div>

      <div className={Styles['column-status']}>
        {privateDevice && (
          <Tooltip
            delay={300}
            placement="top"
            title={constants.TOOLTIP_LABELS.PRIVATE_DEVICE}
          >
            <span className="mr-1.5">
              <IconPrivateDeviceFill className="w-2 h-2 fill-current text-green-600" />
            </span>
          </Tooltip>
        )}

        {!isFreeTrial && (
          <Tooltip
            delay={300}
            placement="left"
            title={(pinned ?
              constants.TOOLTIP_LABELS.PINNED_BUTTON_ACTIVE :
              constants.TOOLTIP_LABELS.PINNED_BUTTON_INACTIVE
            )}
          >
            <button
              aria-label={`${pinned ? 'Unpin' : 'Pin'} Device`}
              className={Styles['button-pinned']}
              data-qa="device-list-item-pinned-button"
              onClick={(event) => {
                event.stopPropagation();
                onPinnedClick();
              }}
              type="button"
            >
              {pinned ?
                <IconUnpinFill className="align-middle fill-current h-2 text-blue-700 w-2" /> :
                <IconPinFill className="align-middle fill-current h-2 text-gray-400 w-2" />}
            </button>
          </Tooltip>
        )}

        <div className={context('hover-buttons-wrapper')}>
          <button
            className={context('hover-button', 'hover-button-detail', 'cursor-pointer', 'text-blue-700')}
            data-qa="device-list-item-details-button"
            onClick={(event) => {
              event.stopPropagation();
              onDetailsClick();
            }}
            type="button"
          >
            <IconEyeFill className={`${Styles['hover-button-icon']} fill-current text-blue-700`} />
            <span className={Styles['hover-button-text']}>
              Details
            </span>
          </button>

          <Tooltip
            delay={300}
            isOpen={!isDevicePermitted ? undefined : false}
            placement="top"
            title={enableAdvise}
          >
            <div>
              <button
                className={context('hover-button', 'hover-button-launch', 'relative', 'cursor-pointer text-blue-700', {
                  'text-gray-600 cursor-not-allowed': isButtonDisabled,
                })}
                data-qa="device-list-item-start-test-button"
                disabled={isButtonDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onLaunchClick();
                }}
                type="button"
              >
                <IconManualFill className={`${Styles['hover-button-icon']} fill-current`} />
                <span className={Styles['hover-button-text']}>
                  Start Test
                </span>
              </button>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default DeviceListItem;
